// TroubleTipCard — 困りごとのコツ (AA) の小さなカード。モーダルにしない (画面をふさがない)。
// docs/_wip/aa-default-and-trouble-tips-2026-10-27.md §2。
//
// 配置: 右下のフィードバックのタカミンアイコン (fixed right-4 bottom-4、52px) のすぐ左。
// Classic / Modern / スマホで共通 (RoomLayout 直下に置く)。
//
// ゴーストクリック対策 (oekaki/CLAUDE.md「タッチのゴーストクリック」): ペンアップ直後に出るので、
// そのタッチの互換 click が着弾しても効かないよう、カード内で pointerdown が始まった click だけ
// 受理する (shouldAcceptCardClick)。出るたびに mount し直すので armed は false から始まる。

import { useRef } from 'react';

import { shouldAcceptCardClick } from '../domain/troubleTips.js';

export const AA_TIP_HELP_URL = 'https://www.takamin.com/help/#draw.antialias';

export interface TroubleTipCardProps {
  readonly onAccept: () => void;
  readonly onDismiss: () => void;
  /** 「くわしく」のリンクを (ゴーストでなく) 実際に開いたとき。計装用。 */
  readonly onDetail?: () => void;
}

export const TroubleTipCard = ({
  onAccept,
  onDismiss,
  onDetail,
}: TroubleTipCardProps): React.ReactNode => {
  const armedRef = useRef(false);
  const guard =
    (action: () => void) =>
    (e: React.MouseEvent): void => {
      if (!shouldAcceptCardClick(armedRef.current, e.detail)) {
        e.preventDefault();
        return;
      }
      action();
    };

  return (
    <section
      aria-label="線をなめらかにするヒント"
      data-testid="trouble-tip-aa"
      onPointerDownCapture={() => {
        armedRef.current = true;
      }}
      className="fixed right-[84px] bottom-[60px] z-30 sm:bottom-4 w-[min(320px,calc(100vw-100px))] rounded-lg border border-[var(--color-accent)] bg-[var(--color-panel)] p-3 text-[var(--color-text)] text-xs shadow-lg ring-1 ring-black/10"
    >
      <p className="mb-1 font-bold text-[var(--color-accent)] text-sm">線がギザギザに見えますか?</p>
      <img
        src="/tips/antialias-compare.png"
        alt="細い線を大きくしたところ。左の「いまの線」はカクカク、右の「なめらか」はふちがやわらかい。"
        width={972}
        height={550}
        className="mb-2 h-auto w-full rounded border border-[var(--color-border)]"
      />
      <p className="mb-1 leading-relaxed">
        「なめらか」にすると、細い線がやわらかく見えます。あとから設定でもどせます。
      </p>
      <p className="mb-2 text-[var(--color-text-muted)] leading-relaxed">
        ドット絵のようにくっきり描きたいなら、このままで大丈夫です。
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={guard(onAccept)}
          className="rounded bg-[var(--color-accent)] px-3 py-1.5 font-bold text-[var(--color-panel)] hover:brightness-110"
        >
          なめらかにする
        </button>
        <button
          type="button"
          onClick={guard(onDismiss)}
          className="rounded border border-white/30 bg-[var(--color-bg-soft)] px-3 py-1.5 hover:bg-[var(--color-bg)]"
        >
          このままでいい
        </button>
        <a
          href={AA_TIP_HELP_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            if (!shouldAcceptCardClick(armedRef.current, e.detail)) {
              e.preventDefault();
              return;
            }
            onDetail?.();
          }}
          className="text-[var(--color-accent)] underline hover:no-underline"
        >
          くわしく
        </a>
      </div>
    </section>
  );
};
